import { isModelNumberArray, isNestedCategory } from "./TableModelNumbers.helper"
import type { TModelNumber, TModelNumbersData, TNestedCategory, TSubCategory } from "types/modelNumbers.type"

export type TModelNumbersSearchResult = {
	/** Same shape as the input, with non-matching model numbers and empty branches removed. */
	data: TModelNumbersData
	/** Model numbers kept. */
	matches: number
	/** Keys of the top-level panels that still hold model numbers, for auto-expanding them. */
	matchedTopLevelKeys: string[]
}

const matchesTerm = (modelNumber: TModelNumber, term: string): boolean =>
	Boolean(modelNumber.model_number?.toLowerCase().includes(term))

const filterSubCategory = (subCategory: TSubCategory, term: string): TSubCategory => {
	const kept: TSubCategory = {}

	Object.entries(subCategory).forEach(([name, modelNumbers]) => {
		const matching = (modelNumbers || []).filter((modelNumber) => matchesTerm(modelNumber, term))
		if (matching.length) kept[name] = matching
	})

	return kept
}

const filterNestedCategory = (nested: TNestedCategory, term: string): TNestedCategory => {
	const kept: TNestedCategory = {}

	Object.entries(nested).forEach(([name, subCategory]) => {
		const matching = filterSubCategory(subCategory, term)
		if (Object.keys(matching).length) kept[name] = matching
	})

	return kept
}

const countBranch = (branch: TNestedCategory | TModelNumber[]): number => {
	if (isModelNumberArray(branch)) return branch.length
	if (!isNestedCategory(branch)) return 0

	return Object.values(branch).reduce<number>(
		(total, subCategory) =>
			total + Object.values(subCategory).reduce<number>((sum, modelNumbers) => sum + (modelNumbers?.length || 0), 0),
		0,
	)
}

/** Total model numbers across the payload, for the "N of M" hint next to the search box. */
export const countModelNumbers = (data?: TModelNumbersData): number =>
	Object.values(data || {}).reduce<number>((total, branch) => total + countBranch(branch), 0)

/**
 * Narrows the payload to the model numbers matching `term`, keeping its shape so the
 * collapse and table builders need no knowledge of searching.
 *
 * Filtering the data rather than the rendered rows is required because the large
 * "Without category" table is virtualized: only a handful of rows exist in the DOM, so
 * the browser's own Ctrl+F cannot find the rest.
 *
 * An empty or whitespace-only term returns the input untouched.
 */
export const filterModelNumbers = (data: TModelNumbersData | undefined, term: string): TModelNumbersSearchResult => {
	const normalized = term.trim().toLowerCase()

	if (!data || !normalized) {
		return { data: data || {}, matches: countModelNumbers(data), matchedTopLevelKeys: [] }
	}

	const filtered: TModelNumbersData = {}

	Object.entries(data).forEach(([topLevelKey, branch]) => {
		if (isModelNumberArray(branch)) {
			const matching = branch.filter((modelNumber) => matchesTerm(modelNumber, normalized))
			if (matching.length) filtered[topLevelKey] = matching
			return
		}

		if (isNestedCategory(branch)) {
			const matching = filterNestedCategory(branch, normalized)
			if (Object.keys(matching).length) filtered[topLevelKey] = matching
		}
	})

	return { data: filtered, matches: countModelNumbers(filtered), matchedTopLevelKeys: Object.keys(filtered) }
}
